import React, { ReactNode, useMemo } from 'react';
import { ApolloError, gql } from '@apollo/client';
import { StatusCodes } from 'http-status-codes';
import { Breakpoint, Constrain, Paginator } from '@moda/om';
import loadable from '@loadable/component';
import { compact } from '@moda/portal-stanchions';
import { SortOption, DEFAULT_SORT_OPTIONS, PRODUCTS_PER_PAGE } from '../../constants';
import { usePLPParams } from '../../hooks/usePLPParams';
import { BackToTop } from '../BackToTop/BackToTop';
import { Loading } from '../Loading';
import { LazyGrid, GridLoading, PlaceholderCell } from '../Grid';
import {
  PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT,
  PAGE_FILTER_FRAGMENT,
  PAGE_FILTER_FACET_FRAGMENT,
  PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT,
  PAGE_FILTER_SIZE_FACET_FRAGMENT
} from '../PageFilter';
import { VARIANT_CELL_FRAGMENT } from '../Grid/GridCell/VariantCell/VariantCell';
import { SchemaOrgProductListing, SCHEMA_ORG_PRODUCT_LISTING_FRAGMENT } from '../SchemaOrg';
import { ProductListPageResultFragment, GuidePageQuery } from '../../generated/types';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { PageMeta } from '../PageMeta';
import { useFilterEngagementTracking } from '../../hooks/useFilterEngagementTracking';
import { usePLPTracking } from '../../hooks/usePLPTracking';
import { useAdminMode } from '../AdminMode/AdminModeProvider';
import { tc } from '../../lib/trackingContext';
import { RedirectWithStatusCode } from '../RedirectWithStatusCode';
import { ALGORITHMS, RecommendationsModule } from '../RecommendationsPumoModule';
import { DIVIDER_LAYOUT_TYPES } from '../DividerPumoModule';
import { ProductListingDesktop } from './ProductListingDesktop';
import { ProductListingMobile } from './ProductListingMobile';
import { ProductListingEmptyStateSubcategoryCarousel } from './ProductListingEmptyStateSubcategoryCarousel';
import { useRedirectLegacyPLPQueryParams } from './useRedirectLegacyPLPQueryParams';
import { ProductListingEmptyState } from './ProductListingEmptyState';

import './ProductListing.scss';

type DisplayPage = NonNullable<GuidePageQuery['displayPage']>;

export const PRODUCT_LIST_PAGE_VARIANT_EDGE_FRAGMENT = gql`
  fragment ProductListPageVariantEdgeFragment on VariantEdge {
    node {
      ...VariantCellFragment
      ...SchemaOrgProductListingFragment
    }
  }
  ${VARIANT_CELL_FRAGMENT}
  ${SCHEMA_ORG_PRODUCT_LISTING_FRAGMENT}
`;

export const PRODUCT_LIST_PAGE_RESULT_FRAGMENT = gql`
  fragment ProductListPageResultFragment on CollectionResult {
    appliedFilters: applied_filters
    attributes {
      ...PageFilterNameSlugFacetFragment
    }
    availability {
      ...PageFilterAvailabilityFacetFragment
    }
    designers {
      ...PageFilterNameSlugFacetFragment
    }
    filterColors: filter_colors {
      ...PageFilterFacetFragment
    }
    materials {
      ...PageFilterNameSlugFacetFragment
    }
    redirectUrl: redirect_url
    markdownPercentages: rounded_markdown_percentages {
      ...PageFilterFacetFragment
    }
    saleTags: sale_tags {
      ...PageFilterFacetFragment
    }
    sizes {
      ...PageFilterSizeFacetFragment
    }
    totalItems: total_items
    variants(after: $after) {
      edges {
        ...ProductListPageVariantEdgeFragment
      }
    }
    ...PageFilterFragment
  }
  ${PRODUCT_LIST_PAGE_VARIANT_EDGE_FRAGMENT}
  ${PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT}
  ${PAGE_FILTER_FACET_FRAGMENT}
  ${PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT}
  ${PAGE_FILTER_SIZE_FACET_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
`;

type ProductListingProps = {
  title: string;
  loading: boolean;
  headerText?: string;
  error?: ApolloError;
  description?: string;
  showPLPBreadcrumbs?: boolean;
  availableSorts?: SortOption[];
  displayPage?: DisplayPage | null;
  queryResult?: ProductListPageResultFragment | null;
  belowHeader?: ReactNode;
  children?: React.ReactNode;
};

export type ProductListingPath = [string, string];

const getRecommendationsIdentifier = (identifier: string) =>
  identifier.replace(/[^a-zA-Z0-9-_]/g, '-');

const VariantCell = loadable(() => import('../Grid/GridCell/VariantCell/VariantCell'), {
  resolveComponent: exports => exports.VariantCell,
  fallback: <PlaceholderCell />
});

const AdminModePLPScoringInfo = loadable(() => import('../AdminMode/AdminModePLPScoringInfo'), {
  resolveComponent: exports => exports.AdminModePLPScoringInfo
});

export const ProductListing: React.FC<ProductListingProps> = ({
  title,
  description,
  headerText,
  queryResult,
  availableSorts = DEFAULT_SORT_OPTIONS,
  displayPage,
  error,
  loading,
  children,
  showPLPBreadcrumbs = true,
  belowHeader = null
}) => {
  const {
    params: { page, q: searchQuery },
    currentSort,
    goToPage,
    changeSort
  } = usePLPParams(availableSorts);

  const onGoToPage = (page: number) => {
    goToPage(page);
    trackClick(page);
  };

  const { enabled: adminModeEnabled } = useAdminMode();

  const variants = useMemo(
    () =>
      compact(
        (queryResult?.variants?.edges || [])
          .filter(edge => Boolean(edge?.node))
          .map(edge => edge && edge.node)
      ),
    [queryResult]
  );

  useFilterEngagementTracking({
    variants,

    loading,
    availableSorts
  });

  useRedirectLegacyPLPQueryParams();

  usePLPTracking({ variants, title, filters: queryResult?.appliedFilters });

  const recentlyViewedIdentifier = useMemo(
    () => getRecommendationsIdentifier(`plp-recently-viewed-${title}`),
    [title]
  );

  if (error) {
    throwQueryError(error);
  }

  if (!queryResult) {
    return <Loading />;
  }

  if (queryResult.redirectUrl)
    return (
      <RedirectWithStatusCode
        statusCode={StatusCodes.MOVED_TEMPORARILY}
        to={queryResult.redirectUrl}
      />
    );

  const shouldPreserveBreadcrumbSpace = Boolean(
    showPLPBreadcrumbs && searchQuery && !queryResult.totalItems
  );
  const isEmptySearchResult = Boolean(searchQuery && !queryResult.totalItems);
  const shouldShowPLPBreadcrumbs = showPLPBreadcrumbs && !shouldPreserveBreadcrumbSpace;
  const totalPages = Math.ceil((queryResult.totalItems || 0) / PRODUCTS_PER_PAGE);

  const trackClick = (page: number) =>
    tc.track('Pagination Clicked', { addToTrackingEvent: { label: String(page) || '' } });

  const emptyStateRecommendationModules = isEmptySearchResult ? (
    <>
      <ProductListingEmptyStateSubcategoryCarousel />
      <RecommendationsModule
        algorithm={ALGORITHMS.RECENTLY_VIEWED}
        identifier={recentlyViewedIdentifier}
        divider={{
          layoutType: DIVIDER_LAYOUT_TYPES.LINE_ABOVE,
          text: 'Recently Viewed'
        }}
      />
    </>
  ) : null;

  const content = (
    <div className="ProductListing__content">
      <LazyGrid className="ProductListing__grid">
        {variants.map((variant, index) => (
          <VariantCell key={variant.id} variant={variant} selfPosition={index + 1} />
        ))}
      </LazyGrid>

      {children}

      {queryResult?.totalItems ? (
        <Paginator
          className="ProductListing__pagination"
          totalPages={totalPages}
          selectedPage={page}
          onGoToPage={onGoToPage}
        />
      ) : (
        <ProductListingEmptyState className="ProductListing__empty-state" />
      )}

      <GridLoading show={loading} />
    </div>
  );

  return (
    <>
      <PageMeta title={title} description={description} paginated totalPages={totalPages} />
      <SchemaOrgProductListing variants={variants} />
      <Constrain>
        <div className="ProductListing">
          {adminModeEnabled && <AdminModePLPScoringInfo />}

          <Breakpoint gt="md">
            <ProductListingDesktop
              availableSorts={availableSorts}
              currentSort={currentSort}
              headerText={headerText}
              onSortChanged={changeSort}
              queryResult={queryResult}
              displayPage={displayPage}
              showPLPBreadcrumbs={shouldShowPLPBreadcrumbs}
              preserveBreadcrumbSpace={shouldPreserveBreadcrumbSpace}
              hideControls={isEmptySearchResult}
              belowHeader={belowHeader}
              belowLayout={emptyStateRecommendationModules}
            >
              {content}
            </ProductListingDesktop>
          </Breakpoint>

          <Breakpoint lt="md">
            <ProductListingMobile
              availableSorts={availableSorts}
              currentSort={currentSort}
              headerText={headerText}
              onSortChanged={changeSort}
              queryResult={queryResult}
              displayPage={displayPage}
              showPLPBreadcrumbs={shouldShowPLPBreadcrumbs}
              preserveBreadcrumbSpace={shouldPreserveBreadcrumbSpace}
              hideActions={isEmptySearchResult}
              belowHeader={belowHeader}
              belowLayout={emptyStateRecommendationModules}
            >
              {content}
            </ProductListingMobile>
          </Breakpoint>
        </div>
      </Constrain>
      <BackToTop />
    </>
  );
};
